// DashboardAdmin.jsx - Version avec arborescence complète (modules, évaluations standard, évaluations enrichies)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  Users, Building2, GraduationCap, ChevronDown, ChevronRight,
  UserCheck, Loader2, RefreshCw, Search, Mail, Hash, Briefcase,
  Clock, BookOpen, User, CheckCircle, XCircle, Award, FileQuestion,
  Layers, FileText, Video, Code, Link
} from 'lucide-react';
import { APP_CONFIG } from '../../config/appConfig';

// ============================================================
// Composant TreeItem (récursif) – gère tous les types
// ============================================================
const TreeItem = ({
  item,
  level = 0,
  expanded,
  onToggle,
  type,
  searchTerm
}) => {
  const [isExpanded, setIsExpanded] = useState(expanded || false);

  // Détermine si l'élément a des enfants
  const hasChildren = (() => {
    switch (type) {
      case 'client':
        return (item.formations?.length > 0) ||
               (item.evaluationsStandard?.length > 0) ||
               (item.evaluationsEnrichies?.length > 0);
      case 'formation':
        return (item.etudiants?.length > 0) || (item.modules?.length > 0);
      case 'module':
        return false; // Les modules n'ont pas d'enfants dans cette vue
      case 'evaluationStandard':
      case 'evaluationRich':
        return item.etudiants?.length > 0;
      case 'etudiant':
        return false;
      default:
        return false;
    }
  })();

  const handleToggle = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
      if (onToggle) onToggle(item.id, type, !isExpanded);
    }
  };

  // Icône selon le type
  const getIcon = () => {
    switch (type) {
      case 'client':
        return <Building2 size={16} className="text-indigo-600" />;
      case 'formation':
        return <GraduationCap size={16} className="text-emerald-600" />;
      case 'module':
        return <Layers size={16} className="text-cyan-600" />;
      case 'evaluationStandard':
        return <FileQuestion size={16} className="text-amber-600" />;
      case 'evaluationRich':
        return <FileQuestion size={16} className="text-purple-600" />;
      case 'etudiant':
        return <Users size={16} className="text-blue-600" />;
      default:
        return null;
    }
  };

  // Couleur de fond de l'icône selon le type
  const getIconBg = () => {
    switch (type) {
      case 'client': return 'bg-indigo-50';
      case 'formation': return 'bg-emerald-50';
      case 'module': return 'bg-cyan-50';
      case 'evaluationStandard': return 'bg-amber-50';
      case 'evaluationRich': return 'bg-purple-50';
      case 'etudiant': return 'bg-blue-50';
      default: return 'bg-slate-50';
    }
  };

  // Couleur de statut
  const getStatusColor = (statut) => {
    if (!statut) return 'bg-slate-100 text-slate-700';
    const statusStr = statut.toUpperCase();
    switch (statusStr) {
      case 'ACTIF':
      case 'ACTIVE':
        return 'bg-emerald-100 text-emerald-700';
      case 'INACTIF':
      case 'INACTIVE':
        return 'bg-red-100 text-red-700';
      case 'EN_COURS':
      case 'PENDING':
        return 'bg-blue-100 text-blue-700';
      case 'TERMINE':
      case 'COMPLETED':
        return 'bg-purple-100 text-purple-700';
      case 'INSCRIT':
      case 'ENROLLED':
        return 'bg-amber-100 text-amber-700';
      case 'BROUILLON':
        return 'bg-gray-200 text-gray-700';
      case 'PUBLIEE':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusText = (statut) => {
    if (!statut) return 'Non défini';
    const statusStr = statut.toUpperCase();
    switch (statusStr) {
      case 'ACTIF':
      case 'ACTIVE': return 'Actif';
      case 'INACTIF':
      case 'INACTIVE': return 'Inactif';
      case 'EN_COURS':
      case 'PENDING': return 'En cours';
      case 'TERMINE':
      case 'COMPLETED': return 'Terminé';
      case 'INSCRIT':
      case 'ENROLLED': return 'Inscrit';
      case 'BROUILLON': return 'Brouillon';
      case 'PUBLIEE': return 'Publiée';
      default: return statut;
    }
  };

  // Mise en surbrillance de la recherche
  const highlightText = (text, search) => {
    if (!search || !text) return text;
    const parts = String(text).split(new RegExp(`(${search})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === search.toLowerCase() ?
        <span key={i} className="bg-yellow-200 font-bold">{part}</span> :
        part
    );
  };

  // Nom affiché
  const itemName = item.nom || item.titre || `${item.prenom || ''} ${item.nom || ''}`.trim() || `Item ${item.id}`;
  const itemEmail = item.email;
  const itemStatut = item.statut || item.status;

  // Informations supplémentaires (badges, métadonnées)
  const renderItemInfo = () => {
    const infoItems = [];

    // Email
    if (itemEmail) {
      infoItems.push(
        <span key="email" className="flex items-center gap-1 text-[9px] text-slate-400">
          <Mail size={10} />
          {highlightText(itemEmail, searchTerm)}
        </span>
      );
    }

    // Code
    if (item.code) {
      infoItems.push(
        <span key="code" className="flex items-center gap-1 text-[9px] text-slate-400">
          <Hash size={10} />
          {highlightText(item.code, searchTerm)}
        </span>
      );
    }

    // Client : secteur, nb formations, nb évaluations
    if (type === 'client') {
      if (item.secteur) {
        infoItems.push(
          <span key="secteur" className="flex items-center gap-1 text-[9px] text-slate-400">
            <Briefcase size={10} />
            {item.secteur}
          </span>
        );
      }
      if (item.formations?.length) {
        infoItems.push(
          <span key="nbFormations" className="flex items-center gap-1 text-[9px] text-indigo-400 bg-indigo-50 px-2 py-0.5 rounded-full">
            <BookOpen size={10} />
            {item.formations.length} formation{item.formations.length > 1 ? 's' : ''}
          </span>
        );
      }
      const nbEvals = (item.evaluationsStandard?.length || 0) + (item.evaluationsEnrichies?.length || 0);
      if (nbEvals) {
        infoItems.push(
          <span key="nbEvaluations" className="flex items-center gap-1 text-[9px] text-amber-400 bg-amber-50 px-2 py-0.5 rounded-full">
            <FileQuestion size={10} />
            {nbEvals} évaluation{nbEvals > 1 ? 's' : ''}
          </span>
        );
      }
    }

    // Formation : nb modules, nb étudiants
    if (type === 'formation') {
      if (item.nbModules !== undefined) {
        infoItems.push(
          <span key="nbModules" className="flex items-center gap-1 text-[9px] text-cyan-400 bg-cyan-50 px-2 py-0.5 rounded-full">
            <Layers size={10} />
            {item.nbModules} module{item.nbModules > 1 ? 's' : ''}
          </span>
        );
      }
      if (item.nbEtudiants !== undefined) {
        infoItems.push(
          <span key="nbEtudiants" className="flex items-center gap-1 text-[9px] text-blue-400 bg-blue-50 px-2 py-0.5 rounded-full">
            <Users size={10} />
            {item.nbEtudiants} étudiant{item.nbEtudiants > 1 ? 's' : ''}
          </span>
        );
      }
    }

    // Module : typeModule + évaluation liée éventuelle
    if (type === 'module') {
      const typeModule = item.typeModule || 'PDF';
      let iconModule = <FileText size={10} />;
      if (typeModule === 'VIDEO') iconModule = <Video size={10} />;
      else if (typeModule === 'JSON' || typeModule === 'JSON_RICH') iconModule = <Code size={10} />;
      else if (typeModule === 'EVALUATION') iconModule = <Link size={10} />;

      infoItems.push(
        <span key="typeModule" className="flex items-center gap-1 text-[9px] text-cyan-600 bg-cyan-50 px-2 py-0.5 rounded-full">
          {iconModule}
          {typeModule}
        </span>
      );
      // Afficher l'évaluation liée si présente
      if (item.evaluation) {
        infoItems.push(
          <span key="linkedEval" className="flex items-center gap-1 text-[9px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
            <FileQuestion size={10} />
            Éval: {item.evaluation.titre || 'ID '+item.evaluation.id}
          </span>
        );
      }
    }

    // Évaluation Standard ou Enrichie : type, score min, nb questions, nb étudiants
    if (type === 'evaluationStandard' || type === 'evaluationRich') {
      infoItems.push(
        <span key="evalType" className="flex items-center gap-1 text-[9px] text-purple-400 bg-purple-50 px-2 py-0.5 rounded-full">
          <Award size={10} />
          {item.type || 'Sommatif'}
        </span>
      );
      if (item.scoreMinimum) {
        infoItems.push(
          <span key="scoreMin" className="flex items-center gap-1 text-[9px] text-slate-400">
            Score min: {item.scoreMinimum}%
          </span>
        );
      }
      if (item.nbQuestions) {
        infoItems.push(
          <span key="nbQuestions" className="flex items-center gap-1 text-[9px] text-slate-400">
            {item.nbQuestions} questions
          </span>
        );
      }
      if (item.nbEtudiants !== undefined) {
        infoItems.push(
          <span key="nbEtudiantsEval" className="flex items-center gap-1 text-[9px] text-blue-400 bg-blue-50 px-2 py-0.5 rounded-full">
            <Users size={10} />
            {item.nbEtudiants} étudiant{item.nbEtudiants > 1 ? 's' : ''}
          </span>
        );
      }
    }

    // Étudiant : progression, date d'inscription
    if (type === 'etudiant') {
      if (item.progression !== undefined) {
        infoItems.push(
          <span key="progression" className="flex items-center gap-1 text-[9px] text-purple-400 bg-purple-50 px-2 py-0.5 rounded-full">
            <Award size={10} />
            {item.progression}%
          </span>
        );
      }
      if (item.dateInscription) {
        infoItems.push(
          <span key="dateInscription" className="flex items-center gap-1 text-[9px] text-slate-400">
            <Clock size={10} />
            {new Date(item.dateInscription).toLocaleDateString('fr-FR')}
          </span>
        );
      }
    }

    return infoItems;
  };

  return (
    <div className="w-full">
      <div
        className={`flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded-xl transition-all cursor-pointer
          ${level > 0 ? 'ml-6 border-l-2 border-slate-200 pl-3' : ''}
          ${type === 'etudiant' ? 'bg-slate-50/50 hover:bg-slate-100' : ''}
          ${type === 'client' ? 'hover:bg-indigo-50/50' : ''}
          ${type === 'formation' ? 'hover:bg-emerald-50/50' : ''}
          ${type === 'module' ? 'hover:bg-cyan-50/50' : ''}
          ${type === 'evaluationStandard' ? 'hover:bg-amber-50/50' : ''}
          ${type === 'evaluationRich' ? 'hover:bg-purple-50/50' : ''}`}
        onClick={handleToggle}
      >
        <div className="w-4 flex-shrink-0">
          {hasChildren && (
            <div className="w-4 h-4 flex items-center justify-center">
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </div>
          )}
        </div>

        <div className={`p-1.5 rounded-lg flex-shrink-0 ${getIconBg()}`}>
          {getIcon()}
        </div>

        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-800 text-sm truncate max-w-[200px]">
            {highlightText(itemName, searchTerm)}
          </span>

          {itemStatut && (
            <span className={`text-[8px] px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${getStatusColor(itemStatut)}`}>
              {getStatusText(itemStatut)}
            </span>
          )}

          {renderItemInfo().length > 0 && (
            <span className="text-[9px] text-slate-300 flex-shrink-0">|</span>
          )}

          <div className="flex items-center gap-2 flex-wrap flex-1">
            {renderItemInfo()}
          </div>
        </div>
      </div>

      {/* Enfants */}
      {isExpanded && hasChildren && (
        <div className="ml-4">
          {type === 'client' && (
            <>
              {/* Formations */}
              {item.formations?.map(formation => (
                <TreeItem
                  key={`f-${formation.id}`}
                  item={formation}
                  type="formation"
                  level={level + 1}
                  searchTerm={searchTerm}
                />
              ))}
              {/* Évaluations Standard indépendantes */}
              {item.evaluationsStandard?.length > 0 && (
                <div className="ml-4 border-l-2 border-amber-200 pl-3 mt-1">
                  <div className="flex items-center gap-2 text-[10px] font-semibold text-amber-600 py-1">
                    <FileQuestion size={14} />
                    Évaluations Standard ({item.evaluationsStandard.length})
                  </div>
                  {item.evaluationsStandard.map(evalItem => (
                    <TreeItem
                      key={`es-${evalItem.id}`}
                      item={evalItem}
                      type="evaluationStandard"
                      level={level + 2}
                      searchTerm={searchTerm}
                    />
                  ))}
                </div>
              )}
              {/* Évaluations Enrichies indépendantes */}
              {item.evaluationsEnrichies?.length > 0 && (
                <div className="ml-4 border-l-2 border-purple-200 pl-3 mt-1">
                  <div className="flex items-center gap-2 text-[10px] font-semibold text-purple-600 py-1">
                    <FileQuestion size={14} />
                    Évaluations Enrichies ({item.evaluationsEnrichies.length})
                  </div>
                  {item.evaluationsEnrichies.map(evalItem => (
                    <TreeItem
                      key={`er-${evalItem.id}`}
                      item={evalItem}
                      type="evaluationRich"
                      level={level + 2}
                      searchTerm={searchTerm}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {type === 'formation' && (
            <>
              {/* Modules */}
              {item.modules?.length > 0 && (
                <div className="ml-4 border-l-2 border-cyan-200 pl-3 mt-1">
                  <div className="flex items-center gap-2 text-[10px] font-semibold text-cyan-600 py-1">
                    <Layers size={14} />
                    Modules ({item.modules.length})
                  </div>
                  {item.modules.map(module => (
                    <TreeItem
                      key={`m-${module.id}`}
                      item={module}
                      type="module"
                      level={level + 2}
                      searchTerm={searchTerm}
                    />
                  ))}
                </div>
              )}
              {/* Étudiants de la formation */}
              {item.etudiants?.length > 0 && (
                <div className="ml-4 border-l-2 border-blue-200 pl-3 mt-1">
                  <div className="flex items-center gap-2 text-[10px] font-semibold text-blue-600 py-1">
                    <Users size={14} />
                    Étudiants ({item.etudiants.length})
                  </div>
                  {item.etudiants.map(etudiant => (
                    <TreeItem
                      key={`e-${etudiant.id}`}
                      item={etudiant}
                      type="etudiant"
                      level={level + 2}
                      searchTerm={searchTerm}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {type === 'evaluationStandard' && item.etudiants?.length > 0 && (
            <div className="ml-4 border-l-2 border-blue-200 pl-3 mt-1">
              <div className="flex items-center gap-2 text-[10px] font-semibold text-blue-600 py-1">
                <Users size={14} />
                Étudiants inscrits ({item.etudiants.length})
              </div>
              {item.etudiants.map(etudiant => (
                <TreeItem
                  key={`es-e-${etudiant.id}`}
                  item={etudiant}
                  type="etudiant"
                  level={level + 2}
                  searchTerm={searchTerm}
                />
              ))}
            </div>
          )}

          {type === 'evaluationRich' && item.etudiants?.length > 0 && (
            <div className="ml-4 border-l-2 border-blue-200 pl-3 mt-1">
              <div className="flex items-center gap-2 text-[10px] font-semibold text-blue-600 py-1">
                <Users size={14} />
                Étudiants inscrits ({item.etudiants.length})
              </div>
              {item.etudiants.map(etudiant => (
                <TreeItem
                  key={`er-e-${etudiant.id}`}
                  item={etudiant}
                  type="etudiant"
                  level={level + 2}
                  searchTerm={searchTerm}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Composant StatsOverview (inchangé)
// ============================================================
const StatsOverview = ({ stats }) => {
  const statItems = [
    { icon: Building2, label: 'Clients', value: stats.totalClients, color: 'indigo' },
    { icon: GraduationCap, label: 'Formations', value: stats.totalFormations, color: 'emerald' },
    { icon: Users, label: 'Étudiants', value: stats.totalEtudiants, color: 'blue' },
    { icon: UserCheck, label: 'Actifs', value: stats.totalActifs, color: 'amber' },
    { icon: FileQuestion, label: 'Évaluations', value: stats.totalEvaluations, color: 'purple' },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
      {statItems.map((item, idx) => {
        const colorMap = {
          indigo: 'bg-indigo-100 text-indigo-600',
          emerald: 'bg-emerald-100 text-emerald-600',
          blue: 'bg-blue-100 text-blue-600',
          amber: 'bg-amber-100 text-amber-600',
          purple: 'bg-purple-100 text-purple-600'
        };
        return (
          <div key={idx} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm hover:shadow-md transition flex items-center gap-3">
            <div className={`p-2 rounded-xl flex-shrink-0 ${colorMap[item.color]}`}>
              <item.icon size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-[8px] font-black text-slate-400 uppercase tracking-wider">{item.label}</p>
              <p className="text-2xl font-black text-slate-800">{item.value || 0}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================================
// Composant principal DashboardAdmin
// ============================================================
export default function DashboardAdmin() {
  const { token } = useAuth();
  const isMounted = useRef(true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [clientsTree, setClientsTree] = useState([]);
  const [stats, setStats] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  // Récupération de l'arborescence complète depuis le backend
  const fetchClientsTree = useCallback(async () => {
    if (!token || !isMounted.current) return;
    try {
      const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/admin/clients/tree`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      let treeData = [];
      if (response.ok) {
        treeData = await response.json();
      }

      if (isMounted.current) {
        setClientsTree(treeData);

        // Calcul des statistiques
        let totalClients = treeData.length;
        let totalFormations = 0;
        let totalEtudiants = 0;
        let totalActifs = 0;
        let totalEvaluations = 0;

        treeData.forEach(client => {
          totalFormations += client.formations?.length || 0;
          totalEvaluations += (client.evaluationsStandard?.length || 0) + (client.evaluationsEnrichies?.length || 0);

          // Étudiants dans les formations
          client.formations?.forEach(formation => {
            totalEtudiants += formation.etudiants?.length || 0;
            formation.etudiants?.forEach(e => {
              const status = (e.statut || '').toUpperCase();
              if (['ACTIF', 'EN_COURS', 'INSCRIT', 'ACTIVE', 'PENDING'].includes(status)) {
                totalActifs++;
              }
            });
          });

          // Étudiants dans les évaluations standard
          client.evaluationsStandard?.forEach(evalItem => {
            totalEtudiants += evalItem.etudiants?.length || 0;
            evalItem.etudiants?.forEach(e => {
              const status = (e.statut || '').toUpperCase();
              if (['ACTIF', 'EN_COURS', 'INSCRIT', 'ACTIVE', 'PENDING'].includes(status)) {
                totalActifs++;
              }
            });
          });

          // Étudiants dans les évaluations enrichies
          client.evaluationsEnrichies?.forEach(evalItem => {
            totalEtudiants += evalItem.etudiants?.length || 0;
            evalItem.etudiants?.forEach(e => {
              const status = (e.statut || '').toUpperCase();
              if (['ACTIF', 'EN_COURS', 'INSCRIT', 'ACTIVE', 'PENDING'].includes(status)) {
                totalActifs++;
              }
            });
          });
        });

        setStats({
          totalClients,
          totalFormations,
          totalEtudiants,
          totalActifs,
          totalEvaluations
        });
      }
    } catch (error) {
      console.error("Erreur récupération données:", error);
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [token]);

  const refreshAll = useCallback(async () => {
    if (!isMounted.current) return;
    setRefreshing(true);
    await fetchClientsTree();
    // Le setLoading(false) est géré dans fetchClientsTree
  }, [fetchClientsTree]);

  useEffect(() => {
    isMounted.current = true;
    refreshAll();
    return () => {
      isMounted.current = false;
    };
  }, [refreshAll]);

  // Filtrage de l'arbre pour la recherche
  const filterTree = (items, search) => {
    if (!search) return items;
    const searchLower = search.toLowerCase();

    return items.filter(item => {
      // Vérifier sur le client lui-même
      const nameMatch = (item.nom || item.titre || '').toLowerCase().includes(searchLower);
      const emailMatch = (item.email || '').toLowerCase().includes(searchLower);
      const codeMatch = (item.code || '').toLowerCase().includes(searchLower);
      if (nameMatch || emailMatch || codeMatch) return true;

      // Vérifier dans les formations
      if (item.formations) {
        const hasFormationMatch = item.formations.some(f =>
          (f.titre || '').toLowerCase().includes(searchLower) ||
          (f.description || '').toLowerCase().includes(searchLower) ||
          (f.code || '').toLowerCase().includes(searchLower) ||
          (f.etudiants || []).some(e =>
            (e.nom || '').toLowerCase().includes(searchLower) ||
            (e.prenom || '').toLowerCase().includes(searchLower) ||
            (e.email || '').toLowerCase().includes(searchLower) ||
            (e.code || '').toLowerCase().includes(searchLower)
          ) ||
          (f.modules || []).some(m =>
            (m.titre || '').toLowerCase().includes(searchLower) ||
            (m.code || '').toLowerCase().includes(searchLower) ||
            (m.typeModule || '').toLowerCase().includes(searchLower) ||
            (m.evaluation?.titre || '').toLowerCase().includes(searchLower)
          )
        );
        if (hasFormationMatch) return true;
      }

      // Vérifier dans les évaluations standard
      if (item.evaluationsStandard) {
        const hasEvalStandardMatch = item.evaluationsStandard.some(e =>
          (e.titre || '').toLowerCase().includes(searchLower) ||
          (e.code || '').toLowerCase().includes(searchLower) ||
          (e.etudiants || []).some(et =>
            (et.nom || '').toLowerCase().includes(searchLower) ||
            (et.prenom || '').toLowerCase().includes(searchLower) ||
            (et.email || '').toLowerCase().includes(searchLower) ||
            (et.code || '').toLowerCase().includes(searchLower)
          )
        );
        if (hasEvalStandardMatch) return true;
      }

      // Vérifier dans les évaluations enrichies
      if (item.evaluationsEnrichies) {
        const hasEvalRichMatch = item.evaluationsEnrichies.some(e =>
          (e.titre || '').toLowerCase().includes(searchLower) ||
          (e.code || '').toLowerCase().includes(searchLower) ||
          (e.etudiants || []).some(et =>
            (et.nom || '').toLowerCase().includes(searchLower) ||
            (et.prenom || '').toLowerCase().includes(searchLower) ||
            (et.email || '').toLowerCase().includes(searchLower) ||
            (et.code || '').toLowerCase().includes(searchLower)
          )
        );
        if (hasEvalRichMatch) return true;
      }

      return false;
    });
  };

  const filteredData = searchTerm ? filterTree(clientsTree, searchTerm) : clientsTree;

  return (
    <div className="p-4 md:p-6 space-y-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 uppercase tracking-tighter">
            Dashboard Administrateur
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Arborescence Clients → Formations (Modules, Étudiants) + Évaluations (Standard & Enrichies)
          </p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
            />
          </div>
          <button
            onClick={refreshAll}
            disabled={refreshing}
            className="p-3 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all disabled:opacity-50"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin text-indigo-600' : 'text-slate-500'} />
          </button>
        </div>
      </div>

      {/* Stats */}
      {!loading && <StatsOverview stats={stats} />}

      {/* Arborescence */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-indigo-600" size={40} />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-3 border-b border-slate-100 bg-slate-50/50">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Building2 size={14} className="text-indigo-600" />
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider">
                  Arborescence ({filteredData.length} clients)
                </span>
              </div>
              <div className="flex items-center gap-3 text-[8px] text-slate-400 flex-wrap">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-indigo-600" /> Clients
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-600" /> Formations
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-cyan-600" /> Modules
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-blue-600" /> Étudiants
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-amber-600" /> Évals Standard
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-purple-600" /> Évals Enrichies
                </span>
              </div>
            </div>
          </div>

          <div className="p-2 max-h-[600px] overflow-y-auto custom-scrollbar">
            {filteredData.length === 0 ? (
              <div className="text-center py-5 text-slate-400">
                <Users size={40} className="mx-auto mb-3 opacity-50" />
                <p className="text-sm font-medium">Aucun client trouvé</p>
                <p className="text-xs mt-1">Ajustez vos filtres ou vérifiez vos données</p>
              </div>
            ) : (
              filteredData.map((client, index) => (
                <TreeItem
                  key={`c-${client.id || index}`}
                  item={client}
                  type="client"
                  level={0}
                  expanded={true}
                  searchTerm={searchTerm}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Footer compact */}
      <div className="bg-slate-800 rounded-2xl p-3 text-center">
        <div className="flex items-center justify-center gap-4 text-[8px] text-slate-400 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Users size={10} />
            <span>Administrateur</span>
          </div>
          <div className="w-px h-4 bg-slate-700" />
          <div className="flex items-center gap-1.5">
            <Building2 size={10} />
            <span>{stats.totalClients || 0} clients</span>
          </div>
          <div className="w-px h-4 bg-slate-700" />
          <div className="flex items-center gap-1.5">
            <GraduationCap size={10} />
            <span>{stats.totalFormations || 0} formations</span>
          </div>
          <div className="w-px h-4 bg-slate-700" />
          <div className="flex items-center gap-1.5">
            <UserCheck size={10} />
            <span>{stats.totalEtudiants || 0} étudiants</span>
          </div>
          <div className="w-px h-4 bg-slate-700" />
          <div className="flex items-center gap-1.5">
            <FileQuestion size={10} />
            <span>{stats.totalEvaluations || 0} évaluations</span>
          </div>
          <div className="w-px h-4 bg-slate-700" />
          <div className="text-slate-500">
            {new Date().toLocaleString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }
      `}</style>
    </div>
  );
}